/**
 * `RawImageRef` — an image reference **as stored**, i.e. one that has NOT
 * passed `resolveStorageUrl`.
 *
 * ## Why the type lives HERE and not next to the resolver
 *
 * The brand has three consumers that must all agree on ONE type identity:
 *   - web domain  `lib/storage/url.ts`      (+ `lib/storage/public-url.ts`)
 *   - mobile port `apps/mobile/lib/storage-url.ts`
 *   - the wire DTOs in this package (`cart.ts`, `listing-meta.ts`, …)
 *
 * `packages/contract` is the only module all three can import (the app's
 * `lib/` is not reachable from mobile, and mobile's `lib/` is not reachable
 * from the web app). A *mirrored* `unique symbol` declaration would produce
 * two mutually-incompatible brands and the DTOs would type-check on neither
 * side, so the declaration is single-sourced here and re-exported by both
 * storage modules — those stay the import path delivery code uses.
 *
 * ## What the brand buys
 *
 * The DB's 7 image SoT columns (`listings.image_urls`, `order_items.image_url`,
 * `messages.image_url`, `profiles.avatar_url`, `profiles.banner_url`,
 * `posts.image_urls`, `production_updates.media_urls`) hold **bucket-relative
 * paths** since phase C. A value read out of one of those columns is only
 * renderable after `resolveStorageUrl`; handing it straight to an absolute-URL
 * sink (`<Image src>`, `new URL`, `fetch`, RN `source={{uri}}`) is a runtime
 * crash or a silent 404.
 *
 * The 2026-07-27 alias sweep found ~30 such sinks that a name-based grep could
 * not see, because the column value travels under an alias
 * (`avatar_url` → `sellerAvatarUrl` → `profileImage`). `RawImageRef` is
 * deliberately **NOT assignable to `string`**, so the compiler makes the next
 * missed alias a build error instead of a prod crash: the only way to get a
 * `string` out of it is `resolveStorageUrl` (or the explicit
 * `rawImageRefValue` escape hatch).
 *
 * A `string & { brand }` would NOT work for this — an intersection with
 * `string` IS assignable to `string`, so every sink would keep compiling.
 * Hence the opaque (non-string) shape below.
 *
 * ## The second job: display ≠ persistence (handoff §9)
 *
 * Fields that are both rendered AND written back to the DB (`photo.preview`
 * web, `PhotoItem.remoteUrl` mobile, `checkout-payload` snapshots,
 * `parseImages`) must stay RAW — resolving at the producer would send absolute
 * URLs back into the column and undo phase C. Typing those fields
 * `RawImageRef` makes assigning a resolved `string` into them a type error.
 *
 * ## Runtime
 *
 * Zero. The value is a plain `string` at runtime; `asRawImageRef` and
 * `rawImageRefValue` are identity casts and the phantom property never exists.
 * JSON serialisation is unaffected — the brand lives only in the declared
 * types on both ends of the wire.
 *
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §9, §11
 * @see lib/storage/url.ts             web resolver (re-exports this brand)
 * @see apps/mobile/lib/storage-url.ts mobile port (re-exports this brand)
 */

import { z } from "zod";

/**
 * Phantom key. `unique symbol` + `declare` → unforgeable and fully erased:
 * there is no runtime property, and no code outside this file can spell it.
 */
declare const RAW_IMAGE_REF: unique symbol;

/**
 * An image reference exactly as it sits in the DB / on the wire: a
 * bucket-relative path (`listings/foo.jpg`), a legacy absolute Supabase URL, an
 * external URL, a `blob:` / `data:` preview, or a site-local `/asset.svg`.
 *
 * Opaque on purpose — pass it to `resolveStorageUrl` to obtain a renderable
 * `string`.
 */
export interface RawImageRef {
  readonly [RAW_IMAGE_REF]: true;
}

/**
 * The ONLY sanctioned way to mint a `RawImageRef`. Apply it at the boundary
 * where a stored value enters app code: a Supabase row read, a Zod
 * `.transform()` on a wire schema, a hand-written DTO adapter.
 *
 * Distributive over nullability: `asRawImageRef(x: string | null)` returns
 * `RawImageRef | null`, so adapters keep their existing null handling.
 *
 * Calling it on an ALREADY-RESOLVED absolute URL is not a type error (both are
 * strings) — but it is a lie, and the value it lies about is the one that gets
 * written back to the DB. Mint at the read boundary, never after a resolve.
 */
export function asRawImageRef<T extends string | null | undefined>(
  value: T,
): T extends string ? RawImageRef : T {
  return value as unknown as T extends string ? RawImageRef : T;
}

/** Array form of {@link asRawImageRef}. */
export function asRawImageRefs(
  values: ReadonlyArray<string>,
): RawImageRef[] {
  return values as unknown as RawImageRef[];
}

/**
 * Escape hatch: the underlying string, WITHOUT resolving.
 *
 * Legitimate uses are the ones where the value is treated as data rather than
 * as a URL — persistence (writing the ref back to a column), equality /
 * dedupe, logging, `JSON.stringify` of a snapshot. Keep the call-site count
 * low: every call is a place where the compiler stops helping.
 *
 * If you are reaching for this to feed an `<img>`, a `new URL(...)`, a
 * `fetch(...)` or an RN `source={{ uri }}` — you want `resolveStorageUrl`.
 */
export function rawImageRefValue<T extends RawImageRef | null | undefined>(
  ref: T,
): T extends RawImageRef ? string : T {
  return ref as unknown as T extends RawImageRef ? string : T;
}

/** Array form of {@link rawImageRefValue}. */
export function rawImageRefValues(
  refs: ReadonlyArray<RawImageRef>,
): string[] {
  return refs as unknown as string[];
}

/**
 * Zod leaf for a wire field carrying a stored image ref: parses as a plain
 * string, but the INFERRED output is `RawImageRef`, so a boundary parse is
 * also the mint. Use it in any request/response schema whose field maps to one
 * of the 7 SoT columns.
 *
 * Deliberately NOT named `*Schema`: the package's eslint fence requires every
 * `export const *Schema` to go through `pinned<T>()`, and this is a leaf, not
 * a contract body. `.transform` adds an identity step at runtime — no
 * behavioural change, no validation change.
 */
export const rawImageRefField = () => z.string().transform(asRawImageRef);
